import React from "react";
import classNames from "classnames";
import {
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    AccordionTrigger,
} from "@design-system/Accordion/Accordion";
import { ArrowDownIcon } from "@icons";
import styles from "./SidePanel.module.scss";

interface Props {
    // Untitled sections render only the framed body.
    label?: string;
    // Anchor for the section nav scrollspy (see invoiceSectionAnchors).
    id?: string;
    // When provided (together with label) the section becomes an accordion
    // pane of the surrounding AccordionRoot: the title row toggles it and the
    // body only renders while open.
    value?: string;
    // Card framing for sections that are not collapsible (the Template group);
    // collapsible sections are always framed.
    hasCard?: boolean;
    // Bleeds the body over the card's side/bottom padding so its content runs
    // edge to edge (the Items table).
    hasFlushBody?: boolean;
    // Rendered in the title row, left of the disclosure arrow, while the
    // section is open. Positioned as a sibling of the disclosure button
    // (buttons cannot nest), so clicking it does not toggle the section.
    headerActions?: React.ReactNode;
    children: React.ReactNode;
}

// A block of the details panel. Sections with a value behave as accordion
// panes (the parent AccordionRoot keeps at most one open); the rest are
// always-expanded.
export const PanelSection: React.FC<Props> = ({
    label,
    id,
    value,
    hasCard,
    hasFlushBody,
    headerActions,
    children,
}) => {
    const bodyClassName = classNames(styles.panelSectionBody, {
        [styles.panelSectionBodyFlush]: hasFlushBody,
    });

    if (label && value !== undefined) {
        return (
            <AccordionItem
                value={value}
                id={id}
                render={<section />}
                className={classNames(
                    styles.panelSection,
                    styles.stepSection,
                    styles.sectionCard,
                    styles.collapsibleSection,
                )}
            >
                <div className={styles.sectionDisclosureWrap}>
                    <AccordionHeader>
                        <AccordionTrigger className={styles.sectionDisclosure}>
                            <div className={styles.groupSubtitle}>{label}</div>
                            <ArrowDownIcon
                                aria-hidden
                                className={styles.subtitleDisclosureIcon}
                            />
                        </AccordionTrigger>
                    </AccordionHeader>
                    {headerActions && (
                        <div className={styles.sectionHeaderActions}>
                            {headerActions}
                        </div>
                    )}
                </div>
                <AccordionPanel className={styles.collapsiblePanel}>
                    <div className={bodyClassName}>{children}</div>
                </AccordionPanel>
            </AccordionItem>
        );
    }

    return (
        <section
            id={id}
            className={classNames(styles.panelSection, styles.stepSection, {
                [styles.sectionCard]: hasCard,
            })}
        >
            {label && (
                <div className={styles.groupSubtitleRow}>
                    <div className={styles.groupSubtitle}>{label}</div>
                </div>
            )}
            <div className={bodyClassName}>{children}</div>
        </section>
    );
};
